import { blobToWebP } from 'webp-converter-browser';

type CompressOptions = {
  maxWidth?: number;
  maxHeight?: number;
  maxSizeKB?: number; // целевой размер после сжатия
  initialQuality?: number; // стартовое качество
  minQuality?: number; // минимально допустимое качество
};

const DEFAULTS: Required<CompressOptions> = {
  maxWidth: 1920,
  maxHeight: 1080,
  maxSizeKB: 800, // ~0.8MB
  initialQuality: 0.8,
  minQuality: 0.5
};

async function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = () => reject(new Error('Unable to load image'));
      img.src = event.target?.result as string;
    };
    reader.onerror = () => reject(new Error('Unable to read file'));
    reader.readAsDataURL(file);
  });
}

async function compressImage(file: File, opts: CompressOptions = {}): Promise<Blob> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Можно загружать только изображения');
  }

  const options = { ...DEFAULTS, ...opts };
  const maxBytes = options.maxSizeKB * 1024;

  const { width: originalWidth, height: originalHeight } = await getImageDimensions(file);

  let targetWidth = originalWidth;
  let targetHeight = originalHeight;

  if (originalWidth > options.maxWidth || originalHeight > options.maxHeight) {
    const widthRatio = options.maxWidth / originalWidth;
    const heightRatio = options.maxHeight / originalHeight;
    const ratio = Math.min(widthRatio, heightRatio);

    targetWidth = Math.round(originalWidth * ratio);
    targetHeight = Math.round(originalHeight * ratio);
  }

  let quality = options.initialQuality;
  let compressed = await blobToWebP(file, {
    quality,
    width: targetWidth,
    height: targetHeight
  });

  while (compressed.size > maxBytes && quality > options.minQuality) {
    quality = +(quality - 0.05).toFixed(2);
    compressed = await blobToWebP(file, {
      quality,
      width: targetWidth,
      height: targetHeight
    });
  }

  return compressed;
}

export { compressImage };
